import React, { useEffect, useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { getSettings } from '../api';
import useSeo from '../hooks/useSeo';

const About = () => {
  useSeo({
    title: 'About — Mansfield, TX Motion Designer & Photographer',
    description: 'Christian D Animator — freelance motion designer, animator, video editor, and photographer based in Mansfield, TX, serving the Dallas–Fort Worth Metroplex. 10+ years bringing stories to life.',
  });

  const [headshot, setHeadshot] = useState('https://images.unsplash.com/photo-1617195737496-bc30194e3a19');

  useEffect(() => {
    getSettings().then((s) => s.headshot_url && setHeadshot(s.headshot_url)).catch(() => {});
  }, []);

  const tools = [
    { name: 'After Effects', category: 'Motion Design' },
    { name: 'Premiere Pro', category: 'Video Editing' },
    { name: 'Blender', category: '3D Animation' },
    { name: 'Illustrator', category: 'Design' },
    { name: 'Photoshop', category: 'Design' }
  ];

  const expertise = [
    'Motion Graphics & 2D/3D Animation',
    'Story-Driven Video Editing',
    'Brand & Event Photography',
    'Explainer Videos & Educational Content',
    'Corporate & Broadcast Production',
    'Social Media Content Creation'
  ];

  return (
    <div className="min-h-screen bg-black text-white pt-16">
      {/* Hero */}
      <section className="py-14 lg:py-20 bg-gradient-to-b from-blue-900/20 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="page-title text-4xl lg:text-6xl font-bold mb-6">
                <span className="bg-gradient-to-r from-blue-400 via-blue-400 to-lime-400 bg-clip-text text-transparent">About Me</span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-300 leading-relaxed mb-6">
                I'm Christian D Animator, a freelance Motion Designer, Animator, Video Editor, and Photographer with over a decade of experience bringing stories to life through visual media.
              </p>
              <p className="text-base sm:text-lg text-gray-400 leading-relaxed mb-6">
                My work is rooted in storytelling, clarity, and collaboration. Whether I'm crafting engaging motion graphics for a product launch, editing a corporate video with perfect pacing, or capturing brand photography with cinematic precision — my goal is always to create work that resonates and drives results.
              </p>
              <p className="text-base sm:text-lg text-gray-400 leading-relaxed">
                I've had the privilege of working with exceptional clients including CRM Studios, Dillard's, American Heart Association, and Voz News, delivering projects that combine technical excellence with creative vision.
              </p>
            </div>

            <div className="relative">
              <div className="rounded-lg overflow-hidden shadow-2xl">
                <img
                  src={headshot}
                  alt="Christian D Animator"
                  className="w-full h-auto aspect-square object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise */}
      <section className="py-14 lg:py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold mb-12 text-center">Areas of Expertise</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {expertise.map((item, index) => (
              <div key={index} className="flex items-start">
                <CheckCircle className="text-blue-400 mr-4 flex-shrink-0 mt-1" size={24} />
                <span className="text-lg text-gray-300">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools */}
      <section className="py-14 lg:py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Tools I Use</h2>
            <p className="text-lg sm:text-xl text-gray-400">Industry-standard software for professional results</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {tools.map((tool, index) => (
              <Card key={index} className="bg-gray-900/50 border-gray-800 hover:border-blue-400 transition-colors">
                <CardContent className="p-6 text-center">
                  <h3 className="text-lg font-semibold mb-2">{tool.name}</h3>
                  <p className="text-sm text-gray-400">{tool.category}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-14 lg:py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold mb-8 text-center">My Approach</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <Card className="bg-gray-900/50 border-gray-800">
              <CardContent className="p-8 text-center">
                <div className="text-5xl font-bold text-blue-400 mb-4">01</div>
                <h3 className="text-xl font-semibold mb-3">Storytelling</h3>
                <p className="text-gray-400">
                  Every project tells a story. I focus on crafting narratives that connect with audiences emotionally and intellectually.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-gray-800">
              <CardContent className="p-8 text-center">
                <div className="text-5xl font-bold text-blue-400 mb-4">02</div>
                <h3 className="text-xl font-semibold mb-3">Clarity</h3>
                <p className="text-gray-400">
                  Complex ideas need clear communication. I bring precision and polish to ensure your message is understood.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900/50 border-gray-800">
              <CardContent className="p-8 text-center">
                <div className="text-5xl font-bold text-blue-400 mb-4">03</div>
                <h3 className="text-xl font-semibold mb-3">Collaboration</h3>
                <p className="text-gray-400">
                  Great work comes from great partnerships. I work closely with clients to understand their vision and exceed expectations.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Experience Highlight */}
      <section className="py-14 lg:py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="grid grid-cols-3 gap-4 sm:gap-8">
            <div>
              <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-blue-400 mb-2">10+</div>
              <p className="text-sm sm:text-base text-gray-400">Years Experience</p>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-blue-400 mb-2">100+</div>
              <p className="text-sm sm:text-base text-gray-400">Projects Delivered</p>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-blue-400 mb-2">50+</div>
              <p className="text-sm sm:text-base text-gray-400">Happy Clients</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;